import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-primary)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-200 disabled:text-gray-500 disabled:border-gray-200 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default:
                    'bg-[var(--color-primary)] text-white shadow hover:bg-[#2E9B96] focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2',
                destructive:
                    'bg-red-500 text-white shadow-sm hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                outline:
                    'border border-[var(--color-border)] bg-white shadow-sm hover:bg-gray-50 hover:text-[var(--color-text)] focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200',
                secondary:
                    'bg-[var(--color-secondary)] text-white shadow-sm hover:bg-[#8A5BC7] focus:ring-2 focus:ring-[var(--color-secondary)] focus:ring-offset-2',
                ghost: 'hover:bg-gray-100 hover:text-[var(--color-text)] focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 disabled:bg-transparent disabled:text-gray-400',
                link: 'text-[var(--color-primary)] underline-offset-4 hover:underline focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 disabled:text-gray-400',
            },
            size: {
                default: 'h-9 px-4 py-2',
                sm: 'h-8 rounded-md px-3 text-xs',
                lg: 'h-10 rounded-md px-8',
                icon: 'h-9 w-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = React.useMemo(() => (asChild ? Slot : 'button'), [asChild]);
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = 'Button';

export { Button };
// eslint-disable-next-line react-refresh/only-export-components
export { buttonVariants };
